import { identify, register } from "@/features/analytics";
import { decodeJwt, deleteCookie, getCookie, setCookie } from "@/shared/utils";
import {
  SIGNUP_FLOW_VERSION,
  SIGNUP_FLOW_VERSION_PROP,
} from "../signup-flow-events.constants";

import {
  COOKIE_KEYS,
  DEFAULT_LANDING_PAGE_CATEGORY,
  LANDING_PAGE_CATEGORIES,
} from "../constants";
import {
  extractQueryParameters,
  getLandingPagePathname,
  getTestingNumber,
  getWebsiteWizardData,
} from "./cookie-management.utils";

/**
 * Determines landing page category based on pathname patterns
 */
export const getLandingPageCategory = (landingPage: string | null): string => {
  if (!landingPage) return DEFAULT_LANDING_PAGE_CATEGORY;

  const pathname = getLandingPagePathname(landingPage);

  for (const [pattern, category] of Array.from(LANDING_PAGE_CATEGORIES)) {
    if (pattern.test(pathname)) {
      return category;
    }
  }

  return DEFAULT_LANDING_PAGE_CATEGORY;
};

/**
 * Registers analytics properties and identifies user
 */
export const registerAnalytics = (): void => {
  const distinctId = getCookie(COOKIE_KEYS.DISTINCT_ID_COOKIE_KEY);
  const cid = getCookie(COOKIE_KEYS.CID);
  const landingPage = getCookie(COOKIE_KEYS.LANDING_PAGE);
  const landingPageCategory = getLandingPageCategory(landingPage);

  // Get UTM data from cookies
  const utmData = document.cookie
    .split(";")
    .reduce((acc: Record<string, string>, cookie) => {
      const cookiePrefix = COOKIE_KEYS.PARAMS_PREFIX;
      if (cookie.indexOf(cookiePrefix) === -1) return acc;

      const [key, value] = cookie.trim().split("=");
      if (key && value) {
        acc[key.replace(cookiePrefix, "")] = value;
      }
      return acc;
    }, {});

  // Identify user if distinct ID exists
  if (distinctId) identify(distinctId);

  // Register bulk properties. The flow-version tag rides on every signup-side
  // event (view signup page, select signup provider, complete signup, …) so the
  // [New Signup Flow] funnels can filter the whole funnel to the new experience.
  const properties = {
    cid,
    landingPage,
    screen: "/sign-up",
    landingPageCategory,
    [SIGNUP_FLOW_VERSION_PROP]: SIGNUP_FLOW_VERSION,
    ...utmData,
  };

  register(properties);
};

/**
 * Gets comprehensive setup data for user onboarding
 */
export const getSetupData = () => {
  const hqTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const payload: Record<string, unknown> = {
    hqTimezone,
    isEditorV4: true,
    isMigratedToReact: Boolean(getCookie("_sd-migrated")),
  };

  // Get tracking cookies
  const gclid = getCookie(COOKIE_KEYS.GCLID);
  const cid = getCookie(COOKIE_KEYS.CID);
  const landingPage = getCookie(COOKIE_KEYS.LANDING_PAGE);
  const wizardEnvironment = getCookie(COOKIE_KEYS.WIZARD_ENVIRONMENT);

  // Process parameters from cookies
  const cookies = document.cookie
    .split(";")
    .filter((cookie) => cookie.indexOf(COOKIE_KEYS.PARAMS_PREFIX) > -1);

  for (const cookie of cookies) {
    const [k, v] = cookie.trim().split("=");
    if (k && v) {
      payload[k.replace(COOKIE_KEYS.PARAMS_PREFIX, "")] = v;
      deleteCookie(k);
    }
  }

  const testingNumber = getTestingNumber();
  const distinctId = getCookie(COOKIE_KEYS.DISTINCT_ID);
  const payloadLandingPage = landingPage === "/" ? "/home" : landingPage;

  // Add core properties
  payload.gclid = gclid;
  payload.cid = cid;
  payload.landingPage = payloadLandingPage;
  payload.landingPageCategory = getLandingPageCategory(landingPage);
  payload.wizardEnvironment = wizardEnvironment;

  if (testingNumber) {
    payload.testingNumber = testingNumber;
  }

  if (distinctId) {
    payload.id = distinctId;
    deleteCookie(COOKIE_KEYS.DISTINCT_ID);
  }

  // Handle invitation token
  const invitation = verifyInvitation();
  if (invitation) {
    payload.invitation_token = invitation;
    deleteCookie(COOKIE_KEYS.INVITATION);
  }

  // Handle website wizard data
  const websiteWizardData = getWebsiteWizardData();
  const websiteWizardDataKeys = Object.keys(websiteWizardData);
  for (const key of websiteWizardDataKeys) {
    payload[key] = websiteWizardData[key as keyof typeof websiteWizardData];
  }

  // Handle docpilot integration
  const wizardDocpilotCookie = getCookie(COOKIE_KEYS.WIZARD_DOCPILOT);
  if (wizardDocpilotCookie) {
    try {
      const docpilotCookiePayload = JSON.parse(wizardDocpilotCookie);
      if (docpilotCookiePayload.isMobileFlow) {
        payload.docpilotPayload = docpilotCookiePayload;
        payload.isDocpilot = true;
      }
    } catch (e) {
      console.error("Failed to parse docpilot cookie:", e);
    }
  }

  return payload;
};

/**
 * Verifies and extracts invitation token
 */
export const verifyInvitation = (): string | null => {
  const hasInvitation = getCookie(COOKIE_KEYS.INVITATION);
  if (!hasInvitation) return null;

  try {
    const parsed = JSON.parse(hasInvitation);
    return parsed.invitation_token || null;
  } catch {
    deleteCookie(COOKIE_KEYS.INVITATION);
    return null;
  }
};

/**
 * Parses query string and handles invitation/verification tokens
 */
export const parseQueryString = (): {
  invitation?: Record<string, unknown>;
  verification?: Record<string, unknown>;
} => {
  const url = window.location.href;
  const data = extractQueryParameters(url);

  // Store tokens in cookies
  if (data.invitation_token) {
    setCookie({
      name: COOKIE_KEYS.INVITATION,
      value: JSON.stringify(data),
      days: 7,
    });
  }
  if (data.verification_token) {
    setCookie({
      name: COOKIE_KEYS.VERIFICATION,
      value: JSON.stringify(data),
      days: 7,
    });
  }

  const invitation = getCookie(COOKIE_KEYS.INVITATION);
  const verification = getCookie(COOKIE_KEYS.VERIFICATION);

  if (!invitation && !verification) {
    return {};
  }

  let parsedData = null;
  if (invitation) {
    try {
      parsedData = decodeJwt(invitation);
    } catch (e) {
      console.error("Error parsing invitation JWT:", e);
    }
  } else if (verification) {
    try {
      parsedData = decodeJwt(verification);
    } catch (e) {
      console.error("Error parsing verification JWT:", e);
    }
  }

  // Clean up URL
  if (
    ["?verification_token", "?invitation_token"].some((param) =>
      url.includes(param),
    )
  ) {
    const newUrl = url.split("?")[0];
    window.history.replaceState({}, document.title, newUrl);
  }

  return {
    invitation: invitation ? parsedData : null,
    verification: verification ? parsedData : null,
  };
};
